import React, { useEffect, useState, useCallback, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { journeyActions } from "store/actions";
import { LoginContext } from "helpers/LoginContext";
import SideBar from "components/common/SideBar/SideBarNew";
import Header from "components/common/Header/Header";
import CircularLoader from "components/common/CircularLoader/CircularLoader";
import { HiOutlineEye } from "react-icons/hi";
import {
  getJourneySessionsList,
  getJourneySessionsLoading,
} from "store/selector/journey.selector";
import Pagination from "components/common/Pagination";
import { formatDateTimeInClientTimezone } from "Utils/utils";

const formatDuration = (seconds) => {
  if (!seconds && seconds !== 0) return "\u2014";
  const s = Number(seconds);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}h ${m}m`;
};

const toLocalDateString = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const getDefaultDateRange = (maxDays = 14) => {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - maxDays);
  return {
    from: toLocalDateString(from),
    to: toLocalDateString(to),
  };
};

const getDaysBetween = (fromStr, toStr) => {
  if (!fromStr || !toStr) return 0;
  const a = new Date(fromStr);
  const b = new Date(toStr);
  return Math.ceil((b - a) / (1000 * 60 * 60 * 24));
};

const UserSessionsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { sideBarPadding } = useContext(LoginContext);

  const loading = useSelector(getJourneySessionsLoading);
  const error = useSelector((state) => state.journey?.get("error"));
  const sessionsList = useSelector(getJourneySessionsList);
  const maxSearchDays = sessionsList?.max_search_days ?? 14;

  const [filters, setFilters] = useState(() => ({
    page: 1,
    per_page: 20,
    search: "",
    ...getDefaultDateRange(14),
  }));

  const [dateRangeError, setDateRangeError] = useState("");

  const fetchData = useCallback(
    (overrides = {}) => {
      const payload = { ...filters, ...overrides };
      if (payload.from && payload.to && getDaysBetween(payload.from, payload.to) > maxSearchDays) {
        return;
      }
      const cleaned = {};
      Object.keys(payload).forEach((k) => {
        if (payload[k] !== "" && payload[k] !== null && payload[k] !== undefined) {
          cleaned[k] = payload[k];
        }
      });
      dispatch(journeyActions.getJourneySessionsRequest(cleaned));
    },
    [dispatch, filters, maxSearchDays]
  );

  useEffect(() => {
    fetchData();
  }, [filters.page]);

  useEffect(() => {
    if (filters.from && filters.to) {
      const days = getDaysBetween(filters.from, filters.to);
      if (days > maxSearchDays) {
        setDateRangeError(`Date range cannot exceed ${maxSearchDays} days.`);
        return;
      }
      setDateRangeError("");
    }
    if (filters.page !== 1) {
      setFilters((prev) => ({ ...prev, page: 1 }));
    } else {
      fetchData();
    }
  }, [filters.from, filters.to, maxSearchDays]);

  useEffect(() => {
    if (filters.search === "") {
      fetchData({ search: "" });
      return;
    }
    const timeoutId = setTimeout(() => {
      fetchData({ search: filters.search, page: 1 });
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [filters.search]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    if (field === "from" || field === "to") setDateRangeError("");
  };

  const handleClearFilters = () => {
    setDateRangeError("");
    setFilters({
      page: 1,
      per_page: 20,
      search: "",
      ...getDefaultDateRange(maxSearchDays),
    });
  };

  const data = sessionsList?.data || [];
  const currentPage = sessionsList?.current_page || 1;
  const lastPage = sessionsList?.last_page || 1;
  const total = sessionsList?.total || 0;
  const perPage = sessionsList?.per_page || filters.per_page || 20;

  const hasActiveFilters = filters.search || filters.from || filters.to;

  return (
    <div className="min-h-full">
      <SideBar />
      <main>
        <div
          className={`${
            sideBarPadding
              ? "lg:pl-[4rem] duration-300 flex flex-col"
              : "lg:pl-64 duration-300 flex flex-col"
          }`}
        >
          <Header
            title="User Sessions"
            desc="Browse user session timelines and journey tracking"
          />

          <div className="w-full">
            {/* Filters Bar */}
            <div className="bg-white border-b px-4 sm:px-6 lg:px-8 py-4 sticky top-20 z-10">
              <p className="text-sm text-gray-500 mb-3">
                Search limited to the last {maxSearchDays} days.
              </p>
              {dateRangeError && (
                <p className="text-sm text-red-600 mb-3">{dateRangeError}</p>
              )}
              {error && (
                <p className="text-sm text-red-600 mb-3">
                  {error?.response?.data?.message || error?.message || String(error)}
                </p>
              )}
              <div className="flex flex-wrap gap-3 items-end">
                {/* Search */}
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Search
                  </label>
                  <input
                    type="text"
                    placeholder="Search by user name..."
                    value={filters.search}
                    onChange={(e) =>
                      handleFilterChange("search", e.target.value)
                    }
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Date From */}
                <div className="min-w-[150px]">
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    From
                  </label>
                  <input
                    type="date"
                    value={filters.from}
                    onChange={(e) =>
                      handleFilterChange("from", e.target.value)
                    }
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Date To */}
                <div className="min-w-[150px]">
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    To
                  </label>
                  <input
                    type="date"
                    value={filters.to}
                    onChange={(e) =>
                      handleFilterChange("to", e.target.value)
                    }
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex items-end gap-2">
                  {hasActiveFilters && (
                    <button
                      onClick={handleClearFilters}
                      className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Results Table */}
            <div className="bg-white max-w-full mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-10">
              <div className="flex flex-col">
                <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
                  <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                    <div className="shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                      <table className="min-w-full divide-y divide-gray-300">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="bg-blue-50 px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                              User
                            </th>
                            <th className="bg-blue-50 px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                              Started
                            </th>
                            <th className="bg-blue-50 px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                              Ended
                            </th>
                            <th className="bg-blue-50 px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                              Duration
                            </th>
                            <th className="bg-blue-50 px-3 py-3.5 text-center text-sm font-semibold text-gray-900">
                              Pages
                            </th>
                            <th className="bg-blue-50 px-3 py-3.5 text-center text-sm font-semibold text-gray-900">
                              Actions
                            </th>
                            <th className="bg-blue-50 px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                              IP Address
                            </th>
                            <th className="bg-blue-50 px-3 py-3.5 text-center text-sm font-semibold text-gray-900">
                              Timeline
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                          {loading ? (
                            <tr>
                              <td colSpan={8} className="bg-gray-50 py-6">
                                <CircularLoader
                                  classes="flex justify-center items-center"
                                  classloader="loader-l"
                                />
                              </td>
                            </tr>
                          ) : data.length > 0 ? (
                            data.map((session) => (
                              <tr
                                key={session.session_id}
                                className="even:bg-white odd:bg-gray-50 hover:bg-blue-50/30 transition-colors"
                              >
                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900 capitalize">
                                  {session.user_name || "\u2014"}
                                </td>
                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                  {formatDateTimeInClientTimezone(session.started_at)}
                                </td>
                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                  {session.ended_at
                                    ? formatDateTimeInClientTimezone(session.ended_at)
                                    : (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                        Active
                                      </span>
                                    )}
                                </td>
                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                  {formatDuration(session.duration_seconds)}
                                </td>
                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-center">
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                    {session.page_views_count || 0}
                                  </span>
                                </td>
                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-center">
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                                    {session.audit_actions_count || 0}
                                  </span>
                                </td>
                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-400 font-mono">
                                  {session.ip_address || "\u2014"}
                                </td>
                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-center">
                                  <button
                                    onClick={() =>
                                      navigate(
                                        `/user-sessions/${session.session_id}`
                                      )
                                    }
                                    className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800"
                                    title="View Timeline"
                                  >
                                    <HiOutlineEye className="w-5 h-5" />
                                    <span className="text-xs">View</span>
                                  </button>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td
                                colSpan={8}
                                className="py-8 px-4 text-center"
                              >
                                <p className="text-gray-500 mb-2">
                                  No sessions found.
                                </p>
                                <p className="text-sm text-gray-400 max-w-md mx-auto">
                                  User sessions are recorded when User Journey Tracking is enabled in Settings → SP Settings. Sessions appear after users browse the app.
                                </p>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Pagination */}
            {data.length > 0 && (
              <div className="bg-white px-3 sm:px-6 lg:px-8 max-w-full mx-auto py-4 sm:py-6 border-t border-gray-200">
                <Pagination
                  page={currentPage}
                  activeListLength={data}
                  limit={perPage}
                  totalList={total}
                  totalPages={lastPage}
                  handlePagination={(page) => {
                    setFilters((prev) => ({ ...prev, page }));
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default UserSessionsPage;
